import _ from 'lodash';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Book } from '../../Types/Book';
import { BookChapter } from '../../Types/BookChapter';
import { StorylineDilemma } from '../../Types/StorylineDilemma';

interface BookChapterContextType {
  book: Book;
  chapter: BookChapter;
  nextChapterId?: string;
  nextChapterGenerationRequestId?: string;
  nextChapterGenerationStartedAt: Date | null;
  registerNextChapterGeneraitonStarting: () => void;
  registerNextChapterGenerationStarted: (
    chapterId: string,
    requestId: string,
  ) => void;
  resetNextChapterGeneration: () => void;
  registerNextChapterDilemmaCustomized: (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => void;
  resetNextChapterDilemmaCustomized: (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => void;
}

interface BookChapterContextProviderProps {
  children: ReactNode;
  book: Book;
  chapter: BookChapter;
}

// Create the context with a default value
const BookChapterContext = createContext<BookChapterContextType | undefined>(
  undefined,
);

/**
 * Provider component that wraps parts of the app that need shared
 * book progress state
 */
export const BookChapterContextProvider: React.FC<
  BookChapterContextProviderProps
> = ({ children, book, chapter }) => {
  const [bookState, setBookState] = useState<Book | undefined>(undefined);
  const [chapterState, setChapterState] = useState<BookChapter | undefined>(
    undefined,
  );
  const [nextChapterId, setNextChapterId] = useState<string | undefined>();
  const [nextChapterGenerationRequestId, setNextChapterGenerationRequestId] =
    useState<string | undefined>();
  const [nextChapterGenerationStartedAt, setChapterGenerationStartedAt] =
    useState<Date | null>(null);

  // Initialize state based on book and chapter props
  useEffect(() => {
    setBookState(book);
    setChapterState(chapter);

    if (!book || !chapter) return;

    // If the book has more chapters, set the next chapter id
    if (chapter.chapterNumber < book.numberOfChapters) {
      const nextChapter = book.chapters[chapter.chapterNumber];
      if (nextChapter) {
        setNextChapterId(nextChapter.id);
        setChapterGenerationStartedAt(nextChapter.createdAt);
      }
    }
  }, [book, chapter]);

  // Start chapter generation
  const registerNextChapterGeneraitonStarting = () => {
    setChapterGenerationStartedAt(new Date());
  };

  // Set chapter generation started with chapter ID and request ID
  const registerNextChapterGenerationStarted = (
    chapterId: string,
    requestId: string,
  ) => {
    setNextChapterId(chapterId);
    setNextChapterGenerationRequestId(requestId);
  };

  // Reset chapter generation state
  const resetNextChapterGeneration = () => {
    setChapterGenerationStartedAt(null);
    setNextChapterId(undefined);
    setNextChapterGenerationRequestId(undefined);
  };

  const registerNextChapterDilemmaCustomized = (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => {
    const newChapterState: BookChapter = _.cloneDeep(chapterState);
    newChapterState.dilemmas = newChapterState.dilemmas.filter(
      (d) => d.id !== dilemma.id,
    );
    newChapterState.dilemmas.push({
      ...dilemma,
      customizedAt: new Date(),
    });
    setChapterState(newChapterState);
  };

  const resetNextChapterDilemmaCustomized = (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => {
    const newChapterState: BookChapter = _.cloneDeep(chapterState);
    newChapterState.dilemmas = chapter.dilemmas;
    setChapterState(newChapterState);
  };

  // Computed property to determine if a chapter is currently generating

  const contextValue: BookChapterContextType = {
    book: bookState,
    chapter: chapterState,
    nextChapterId,
    nextChapterGenerationRequestId,
    nextChapterGenerationStartedAt,
    registerNextChapterGeneraitonStarting,
    registerNextChapterGenerationStarted,
    resetNextChapterGeneration,
    registerNextChapterDilemmaCustomized,
    resetNextChapterDilemmaCustomized,
  };

  return (
    <BookChapterContext.Provider value={contextValue}>
      {children}
    </BookChapterContext.Provider>
  );
};

/**
 * Custom hook to access the book progress context
 */
export const useBookChapterContext = (): BookChapterContextType => {
  const context = useContext(BookChapterContext);

  if (context === undefined) {
    console.warn(
      'useBookChapterContext must be used within a BookChapterContextProvider',
    );
    return {} as BookChapterContextType;
  }

  return context;
};
